/**
 * Tema personalizado de Material-UI
 * Basado en la web oficial de MET Energía España (metenergia.es)
 *
 * Colores extraídos de la web:
 * - Teal MET: #006272 (color principal de botones y acentos)
 * - Navy oscuro: #1a2d3d (barra de navegación, fondos oscuros)
 * - Gradiente ELECTRICIDAD: linear-gradient(135deg, #005562, #007a87)
 * - Gradiente GAS: linear-gradient(135deg, #1a2d3d, #2a5d6e)
 */

import { createTheme } from '@mui/material/styles';

// Colores corporativos MET Energía (web oficial)
export const COLORS = {
  // Colores principales (web oficial MET)
  teal: '#006272',
  tealDark: '#004d5a',
  tealLight: '#007a87',

  navy: '#1a2d3d',
  navyLight: '#243d4f',

  // Alias
  primary: '#006272',
  primaryDark: '#004d5a',
  primaryLight: '#007a87',

  // Gradientes (botones ELECTRICIDAD/GAS)
  gradientElectricidad: 'linear-gradient(135deg, #005562 0%, #007a87 100%)',
  gradientGas: 'linear-gradient(135deg, #1a2d3d 0%, #2a5d6e 100%)',
  gradientPrimary: 'linear-gradient(135deg, #005562 0%, #007a87 100%)',

  // Colores de fondo
  background: '#f4f6f8',
  backgroundLight: '#eef2f5',
  backgroundPaper: '#ffffff',

  // Colores de texto
  textPrimary: '#1a2d3d',
  textSecondary: '#5a6a7a',
  textOnDark: '#ffffff',
  textOnDarkMuted: 'rgba(255,255,255,0.6)',

  // Estados
  success: '#4caf50',
  error: '#e53935',
  warning: '#ff9800',
  info: '#2196f3',
};

const theme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: COLORS.teal,
      dark: COLORS.tealDark,
      light: COLORS.tealLight,
      contrastText: '#ffffff',
    },
    secondary: {
      main: COLORS.navy,
      dark: '#0f1e2b',
      light: COLORS.navyLight,
      contrastText: '#ffffff',
    },
    background: {
      default: COLORS.background,
      paper: COLORS.backgroundPaper,
    },
    text: {
      primary: COLORS.textPrimary,
      secondary: COLORS.textSecondary,
    },
    success: { main: COLORS.success },
    error: { main: COLORS.error },
    warning: { main: COLORS.warning },
    info: { main: COLORS.info },
  },

  typography: {
    fontFamily: [
      'Inter',
      '-apple-system',
      'BlinkMacSystemFont',
      '"Segoe UI"',
      'Roboto',
      '"Helvetica Neue"',
      'Arial',
      'sans-serif',
    ].join(','),
    h1: { fontSize: '2.75rem', fontWeight: 800, color: COLORS.textPrimary, letterSpacing: '0.08em', textTransform: 'uppercase' },
    h2: { fontSize: '2.25rem', fontWeight: 700, color: COLORS.textPrimary, letterSpacing: '0.06em', textTransform: 'uppercase' },
    h3: { fontSize: '1.75rem', fontWeight: 700, color: COLORS.textPrimary, letterSpacing: '0.05em', textTransform: 'uppercase' },
    h4: { fontSize: '1.5rem', fontWeight: 700, color: COLORS.textPrimary, letterSpacing: '0.05em', textTransform: 'uppercase' },
    h5: { fontSize: '1.25rem', fontWeight: 600, color: COLORS.textPrimary, letterSpacing: '0.05em', textTransform: 'uppercase' },
    h6: { fontSize: '1rem', fontWeight: 600, color: COLORS.textPrimary, letterSpacing: '0.05em', textTransform: 'uppercase' },
    body1: { fontSize: '1rem', color: COLORS.textPrimary },
    body2: { fontSize: '0.875rem', color: COLORS.textSecondary },
  },

  shape: { borderRadius: 8 },

  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 6,
          textTransform: 'uppercase',
          fontWeight: 700,
          padding: '10px 24px',
          fontSize: '0.9rem',
          letterSpacing: '0.08em',
        },
        contained: {
          boxShadow: 'none',
          '&:hover': {
            boxShadow: '0 4px 14px rgba(0, 98, 114, 0.35)',
          },
        },
        containedPrimary: {
          backgroundColor: COLORS.teal,
          '&:hover': {
            backgroundColor: COLORS.tealDark,
          },
        },
      },
    },

    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 10,
          backgroundColor: COLORS.backgroundPaper,
          border: '1px solid #e2e8f0',
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.04), 0 1px 2px rgba(0, 0, 0, 0.06)',
        },
      },
    },

    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: 10,
          backgroundColor: COLORS.backgroundPaper,
        },
      },
    },

    MuiTableHead: {
      styleOverrides: {
        root: {
          '& .MuiTableCell-head': {
            backgroundColor: COLORS.teal,
            color: '#ffffff',
            fontWeight: 700,
            fontSize: '0.8rem',
            textTransform: 'uppercase',
            letterSpacing: '0.08em',
          },
        },
      },
    },

    MuiTableRow: {
      styleOverrides: {
        root: {
          '&:nth-of-type(even)': {
            backgroundColor: '#f8fafb',
          },
          '&:hover': {
            backgroundColor: 'rgba(0, 98, 114, 0.04) !important',
          },
        },
      },
    },

    MuiLinearProgress: {
      styleOverrides: {
        root: { borderRadius: 4, height: 10 },
        bar: { backgroundColor: COLORS.teal, borderRadius: 4 },
      },
    },

    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            '& fieldset': { borderColor: '#d0d5dd' },
            '&:hover fieldset': { borderColor: COLORS.tealLight },
            '&.Mui-focused fieldset': { borderColor: COLORS.teal },
          },
        },
      },
    },
  },
});

export default theme;
